import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ReactComponent as AddIcon } from 'resources/images/add.svg';
import { ChakraFabButton, ContentContainer } from 'app/components/Core';
import { useContextMenu } from 'app/components/ChakraTableList';
import { ContextMenuOption } from 'types/general.types';
import { Link } from 'types/api.types';
import { Collaborator, CollaboratorStatus } from 'types/collaborator.types';
import { Song } from 'types/song';
import { ContentContainerType } from 'types/enums';
import { useAppDispatch } from 'app/store';
import { ContactCardDialog } from 'app/components/ContactCardDialog';
import { resendCollaboratorInviteEffect } from 'app/store/SongList/effects';
import { StickyTable } from 'app/components/Core/StickyTable/StickyTable';
import { Row } from '@tanstack/react-table';
import { contextMenuHandler } from 'app/components/Table/ContextMenuButton/utils';
import { useTotalWidth } from 'app/components/Core/StickyTable';
import { AddCollaboratorDialog } from '../AddCollaboratorDialog';
import { LeaveSongDialog } from '../LeaveSongDialog';
import { RemoveCollaboratorDialog } from '../RemoveCollaboratorDialog';
import { CollaboratorRow } from './CollaboratorRow';
import { getColumns } from './utils';

export interface CollaboratorsListProps {
  collaborators?: Collaborator[];
  song: Song;
}

export const CollaboratorsList: React.FC<CollaboratorsListProps> = ({
  collaborators = [],
  song,
}) => {
  const dispatch = useAppDispatch();
  const [addCollaboratorDialog, setAddCollaboratorDialog] = useState(false);
  const [collaboratorToRemove, setCollaboratorToRemove] = useState<
    Collaborator | undefined
  >(undefined);
  const [collaboratorLeave, setCollaboratorLeave] = useState<{
    collaborator: Collaborator;
    leaveLink: Link;
  }>();
  const [openCollaborator, setOpenCollaborator] = useState<Collaborator>();
  const toggleAddCollaborator = () => {
    setAddCollaboratorDialog(!addCollaboratorDialog);
  };
  const acceptedCollaborators = collaborators?.filter(
    (collaborator) => collaborator.status === CollaboratorStatus.ACCEPTED
  );
  const isInvitePending = (collaborator: Collaborator) =>
    collaborator.status === CollaboratorStatus.PENDING;
  const getMenuOptions: (
    collaborator: Collaborator
  ) => ContextMenuOption[] = useCallback(
    (collaborator) => {
      const canLeave = collaborator.isSelf && song._links?.leaveSong;
      const canRemove = Boolean(collaborator._links?.deleteCollaborator);
      const canResend = Boolean(collaborator._links?.resendInvite);
      return [
        ...(canLeave
          ? [
              {
                title: 'Leave Song',
                value: 'LEAVE',
                onClick: () =>
                  setCollaboratorLeave({
                    collaborator,
                    leaveLink: song._links?.leaveSong as Link,
                  }),
              },
            ]
          : []),
        ...(canResend && isInvitePending(collaborator)
          ? [
              {
                title: 'Re-send invite',
                value: 'RE_SEND_INVITE',
                onClick: async () => {
                  await dispatch(resendCollaboratorInviteEffect(collaborator));
                },
              },
            ]
          : []),
        ...(canRemove
          ? [
              {
                title: 'Remove',
                value: 'REMOVE',
                onClick: () => setCollaboratorToRemove(collaborator),
              },
            ]
          : []),
      ];
    },
    [dispatch, song._links?.leaveSong]
  );

  const tableKey = 'access-list';
  const ref = useRef<HTMLDivElement>(null);
  const contextMenuProps = useContextMenu();
  const totalWidth = useTotalWidth(ref);
  const columns = useMemo(() => getColumns({ totalWidth }), [totalWidth]);

  const rowRenderer = useCallback(
    (item: Row<Collaborator>) => {
      const collaborator = item.original;
      const options = getMenuOptions(collaborator);

      const contextHandler =
        options.length === 0
          ? undefined
          : (e) => contextMenuHandler(e, contextMenuProps, options);

      return (
        <CollaboratorRow
          key={collaborator.inviteGuid || collaborator.userGuid}
          collaborator={collaborator}
          dispatch={dispatch}
          contextHandler={contextHandler}
        />
      );
    },
    [contextMenuProps, dispatch, getMenuOptions]
  );

  return (
    <ContentContainer
      type={ContentContainerType.LIST}
      isViewContent
      data-testid="collaborators"
    >
      <AddCollaboratorDialog
        open={addCollaboratorDialog}
        onClose={toggleAddCollaborator}
      />
      <ChakraFabButton
        aria-label="invite collaborator"
        onClick={toggleAddCollaborator}
        leftIcon={<AddIcon />}
      >
        Invite
      </ChakraFabButton>
      <StickyTable<Collaborator>
        data-testid="collaborator-list-items"
        tableKey={tableKey}
        ref={ref}
        data={collaborators}
        columns={columns}
        rowRenderer={rowRenderer}
        contextMenuProps={contextMenuProps}
      />
      <LeaveSongDialog
        dispatch={dispatch}
        context="collaboratorList"
        song={song}
        open={Boolean(collaboratorLeave)}
        onClose={() => setCollaboratorLeave(undefined)}
        isLastCollaborator={acceptedCollaborators?.length === 1}
        leaveLink={collaboratorLeave?.leaveLink}
      />
      <RemoveCollaboratorDialog
        dispatch={dispatch}
        open={Boolean(collaboratorToRemove)}
        onClose={() => setCollaboratorToRemove(undefined)}
        collaborator={collaboratorToRemove}
      />
      {openCollaborator && (
        <ContactCardDialog
          onClose={() => setOpenCollaborator(undefined)}
          isOpen={Boolean(openCollaborator)}
          collaborator={openCollaborator}
        />
      )}
    </ContentContainer>
  );
};
